import { isPublicCatalogPath } from "./public-catalog";

/**
 * Where to land after signing in or up. It travels two ways: router state (`from`, set when the app
 * itself sends a guest to log in) and the `redirectTo` query parameter, which survives a reload, a
 * new tab and the hop from the website. Only same-origin app paths are accepted: a value such as
 * `//evil.example` or `/\evil.example` would leave the app.
 */
const AUTH_PATHS = new Set(["/login", "/registro", "/recuperar", "/restablecer", "/oauth/callback"]);

export function safeReturnPath(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length > 500) return undefined;
  if (!value.startsWith("/") || value.startsWith("//") || value.includes("\\")) return undefined;
  // Control characters (tab, newline) are dropped by URL parsers and could join "/" + "/" again.
  if (/[\u0000-\u001f\u007f]/.test(value)) return undefined;
  const pathname = value.split(/[?#]/, 1)[0] ?? "";
  return AUTH_PATHS.has(pathname) ? undefined : value;
}

export function returnPathFrom(location: { state: unknown; search: string }): string | undefined {
  const fromState = safeReturnPath((location.state as { from?: unknown } | null)?.from);
  return fromState ?? safeReturnPath(new URLSearchParams(location.search).get("redirectTo"));
}

/** `/login` or `/registro` carrying the page to come back to, in a form that survives a reload. */
export function authPath(path: "/login" | "/registro", returnTo: string | undefined): string {
  const safe = safeReturnPath(returnTo);
  return safe ? `${path}?redirectTo=${encodeURIComponent(safe)}` : path;
}

/**
 * Where "Ver viajes sin crear cuenta" leads: back to the trip the guest came from when it is part of
 * the public catalogue, else Explora. A members-only return path (/inicio after opening the app's
 * root) would only bounce the guest back to /login.
 */
export function guestPathFrom(location: { state: unknown; search: string }): string {
  const back = returnPathFrom(location);
  return back && isPublicCatalogPath(back.split(/[?#]/)[0] ?? "") ? back : "/explorar";
}
